import { isNative } from '@/platform/platform';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';

// ──────────────────────────────────────────────────────────────────────────
// Espejo nativo (Capacitor Preferences → SharedPreferences en Android /
// UserDefaults en iOS). El `localStorage` del WebView NO es fiable en apps
// nativas: el sistema puede purgarlo al cerrar/segundo plano y perder la sesión
// (el alumno tiene que re-loguear). Preferences es almacenamiento nativo
// persistente, así que lo usamos como respaldo del localStorage. Todo best-effort
// y solo en nativo; en web es no-op y no cambia nada.
// ──────────────────────────────────────────────────────────────────────────
async function nativeSet(key: string, value: string): Promise<void> {
  if (!isNative()) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key, value });
  } catch {
    /* best-effort: si el plugin no está disponible, seguimos con localStorage */
  }
}

async function nativeRemove(key: string): Promise<void> {
  if (!isNative()) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.remove({ key });
  } catch {
    /* best-effort */
  }
}

async function nativeGet(key: string): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const { value } = await Preferences.get({ key });
    return value ?? null;
  } catch {
    return null;
  }
}

export class TokenService {
  // La sesión es persistente: se guarda en localStorage para que sobreviva al
  // cierre/reapertura de la app y a que el Service Worker abra un contexto
  // nuevo al pulsar una notificación (móvil/PWA) sin forzar re-login. En apps
  // nativas (iOS/Android) se espeja además en Preferences (nativo), porque el
  // localStorage del WebView puede purgarse. Se sigue leyendo también de
  // sessionStorage como fallback para sesiones ya abiertas antes de este cambio.
  // El `rememberMe` se mantiene por compatibilidad de firma pero ya no altera el
  // almacenamiento. El logout limpia todo.
  private static getFromStorage(key: string): string | null {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  }

  static setToken(token: string, _rememberMe = false): void {
    localStorage.setItem(TOKEN_KEY, token);
    void nativeSet(TOKEN_KEY, token);
  }

  static getToken(): string | null {
    return this.getFromStorage(TOKEN_KEY);
  }

  static removeToken(): void {
    sessionStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_KEY);
    void nativeRemove(TOKEN_KEY);
  }

  static setUser(user: unknown, _rememberMe = false): void {
    const serialized = JSON.stringify(user);
    localStorage.setItem(USER_KEY, serialized);
    void nativeSet(USER_KEY, serialized);
  }

  static getUser<T>(): T | null {
    const userStr = this.getFromStorage(USER_KEY);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr) as T;
    } catch {
      return null;
    }
  }

  static removeUser(): void {
    sessionStorage.removeItem(USER_KEY);
    localStorage.removeItem(USER_KEY);
    void nativeRemove(USER_KEY);
  }

  static clearAuth(): void {
    this.removeToken();
    this.removeUser();
  }

  static isAuthenticated(): boolean {
    return this.getToken() !== null;
  }

  /**
   * Sincroniza el almacenamiento web (localStorage) con el nativo (Preferences)
   * al arrancar. En iOS/Android el WebView puede haber purgado el localStorage y
   * perdido la sesión; Preferences la conserva. Reglas:
   *  - localStorage vacío pero Preferences tiene el dato → lo restaura en
   *    localStorage (RECUPERA la sesión purgada, que era el bug de re-login).
   *  - localStorage lo tiene pero Preferences no → siembra Preferences (respaldo
   *    para futuras purgas; cubre sesiones abiertas antes de este cambio).
   * No-op en web. DEBE llamarse ANTES de leer la sesión al arrancar (AuthContext).
   */
  static async hydrateFromNative(): Promise<void> {
    if (!isNative()) return;
    for (const key of [TOKEN_KEY, USER_KEY]) {
      try {
        const local = localStorage.getItem(key);
        const native = await nativeGet(key);
        if (!local && native) {
          localStorage.setItem(key, native);
        } else if (local && !native) {
          await nativeSet(key, local);
        }
      } catch {
        /* best-effort: nunca bloquear el arranque por el respaldo nativo */
      }
    }
  }
}

export default TokenService;
